import React, { useState } from 'react'
import { useQuery } from '@apollo/client'
import { Table, Spinner, Badge, Button } from 'react-bootstrap'
import { GET_CARRIER_IMPORT_BATCHES } from '../../../gql/queries/carrierImportQueries'
import moment from 'moment'

interface CarrierImportBatch {
  id: string
  carrier: {
    id: string
    name: string
  }
  asOfDate: string
  uploadedBy: {
    id: string
    firstName: string
    lastName: string
  }
  uploadedAt: string
  originalFilename: string
  rowCount: number
  processedCount: number
  matchedCount: number
  unmatchedCount: number
  errorsCount: number
}

interface CarrierImportHistoryProps {
  onViewDetails: (batchId: string) => void
  onOpenCarrierImport?: () => void
}

export const CarrierImportHistory: React.FC<CarrierImportHistoryProps> = ({
  onViewDetails,
  onOpenCarrierImport,
}) => {
  const [limit] = useState(50)
  const [offset] = useState(0)

  const { data, loading, error, refetch } = useQuery(GET_CARRIER_IMPORT_BATCHES, {
    variables: {
      limit,
      offset,
      orderBy: '-uploaded_at',
    },
    fetchPolicy: 'network-only', // Always fetch from network, not cache
  })

  if (loading) {
    return (
      <div className='d-flex flex-center' style={{ height: '50vh' }}>
        <Spinner animation='border' />
      </div>
    )
  }

  if (error) {
    console.error('CarrierImportHistory error:', error)
    return (
      <div className='d-flex flex-center' style={{ height: '50vh' }}>
        <div className='text-danger'>Error loading import history: {error.message}</div>
      </div>
    )
  }

  const batches: CarrierImportBatch[] = data?.carrierImportBatches || []
  console.log('CarrierImportHistory - batches:', batches.length, batches)

  return (
    <div className='carrier-import-history'>
      <div className='d-flex justify-content-between align-items-center mb-3'>
        <h5 className='mb-0'>Carrier Import History</h5>
        <div className='d-flex align-items-center gap-2'>
          {onOpenCarrierImport && (
            <Button size='sm' onClick={onOpenCarrierImport}>
              <i className='bi bi-cloud-upload fs-3' /> Carrier Import
            </Button>
          )}
          <Button
            size='sm'
            variant='outline-primary'
            onClick={() => refetch()}
            disabled={loading}
          >
            <i className='bi bi-arrow-clockwise' /> Refresh
          </Button>
        </div>
      </div>
      <div className='table-responsive'>
        <Table striped hover>
          <thead>
            <tr>
              <th>Uploaded</th>
              <th>Carrier</th>
              <th>As-of Date</th>
              <th>Uploaded By</th>
              <th className='text-center'>Total</th>
              <th className='text-center'>Matched</th>
              <th className='text-center'>Unmatched</th>
              <th className='text-center'>Errors</th>
              <th className='text-center'>Actions</th>
            </tr>
          </thead>
          <tbody>
            {batches.length === 0 ? (
              <tr>
                <td colSpan={10} className='text-center'>
                  No import history found
                </td>
              </tr>
            ) : (
              batches.map((batch) => {
                const uploadedByName = `${batch.uploadedBy.firstName} ${batch.uploadedBy.lastName}`
                const uploadedAt = moment(batch.uploadedAt).format('MM/DD/YYYY hh:mm A')
                const asOfDate = batch.asOfDate || 'N/A'

                return (
                  <tr key={batch.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{uploadedAt}</td>
                    <td>{batch.carrier.name}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{asOfDate}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{uploadedByName}</td>
                    <td className='text-center'>
                      <Badge bg='secondary'>{batch.processedCount}</Badge>
                    </td>
                    <td className='text-center'>
                      <Badge bg='success'>{batch.matchedCount}</Badge>
                    </td>
                    <td className='text-center'>
                      <Badge bg='warning' text='dark'>
                        {batch.unmatchedCount}
                      </Badge>
                    </td>
                    <td className='text-center'>
                      <Badge bg='danger'>{batch.errorsCount}</Badge>
                    </td>
                    <td className='text-center' style={{ whiteSpace: 'nowrap' }}>
                      <Button
                        size='sm'
                        variant='outline-primary'
                        onClick={() => onViewDetails(batch.id)}
                        disabled={batch.unmatchedCount === 0}
                      >
                        <i className='bi bi-eye' /> View Details
                      </Button>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </Table>
      </div>
    </div>
  )
}
